import classNames from 'classnames';
import * as _ from 'lodash';
import queryString from 'query-string';
import React, { useState, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import * as api from '../api/api';
import dffLogo from '../assets/img/dff/logo.png';
import * as selectors from '../dff/dff-selectors';
import { selectMount } from '../dff/dff-slice';
import { getFileType } from '../dff/file';
import { FileGroup } from '../file-group/file-group';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { MainMenuComponent } from '../menu/main-menu';
import { MenuComponent } from '../menu/menu';
import { ToolbarAnalyse } from '../toolbar/toolbar-analyse-component';
import { Toolbar } from '../toolbar/toolbar-component';
import { sizeDisplay, PathUtils } from '../utils';

import './dff-analyse.scss';

export interface DffAnalyseStateToProps {
  mount: api.Mount;
}

export interface DffAnalyseDispatchToProps {
  selectMount: (mount: api.Mount) => void;
}

export interface DffAnalyseOwnProps {
  // Router props.match
  match: any;
  location: any;
  history: any;
}

export const SortTypeCount = 'count';
export const SortTypeSize = 'size';
export const SortTypeTsize = 'tsize';
export const SortTypeType = 'type';

export type SortType =
  | typeof SortTypeCount
  | typeof SortTypeSize
  | typeof SortTypeTsize
  | typeof SortTypeType;

export interface FileObject {
  file: api.File;
  mountId: string;
  path: string;
  splittedPath: string[];
  children?: FileObject[];
  remaining?: number;
  descendants?: number;
  isOpen?: boolean;
  size?: number;
  parent?: any;
  showInTree?: boolean;
  isLocked?: boolean;
  tooltip?: string;
  modified?: number;
}

export interface DffStateProps {
  selectedButton: string;
  sort: SortType;
}

const initialState: DffStateProps = {
  selectedButton: '',
  sort: 'count',
};

function reducer(state: DffStateProps, action): DffStateProps {
  switch (action.type) {
    case 'selectButton':
      return { ...initialState, selectedButton: action.payload };
    case 'sortButtonClick':
      return { ...initialState, sort: action.payload };
    default:
      return state;
  }
}

export interface DffAnalyseProps extends DffAnalyseOwnProps, DffStateProps, DffAnalyseStateToProps, DffAnalyseDispatchToProps {

}

export const DffAnalyse: React.FC<DffAnalyseProps> = (props) => {
  const query = queryString.parse(props.location.search)
  const mountId = props.match.params.mountId;
  const rootPath = PathUtils.normalize((query.path || '/') as string);

  const [isDone, setIsDone] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorText, setErrorText] = useState("");
  const [originalDuplicates, setOriginalDuplicates] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [excess, setExcess] = useState(0);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);

  // For right-click menu operations
  const [visible, setVisible] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [activeFile, setActiveFile] = useState(null);

  const mount = useSelector(selectors.mount);

  const [state, dispatch] = useReducer(reducer, initialState);

  const emptyHash = 'd41d8cd98f00b204e9800998ecf8427e';

  const dspGlobal = useDispatch();

  const canFindDuplicates = (mount: api.Mount): boolean => {
    return mount.origin === 'hosted';
  };

  useEffect(() => {
    // abortController is used for aborting this hook on unmount (aborting fetch)
    const abortController = new AbortController();
    const signal = abortController.signal;

    let path = props.location.search;
    if (!mount) {
      fetch(`/api/v2/mounts/${mountId}`)
      .then(results => results.json())
      .then(data => {
        dspGlobal(selectMount(data));
      });
    }

    fetch(`/content/api/v2/mounts/${mountId}/files/listrecursive?path=${encodeURIComponent(rootPath)}`, { signal: signal })
      .then(res => {
        if (!res.ok) {
          // throw Error(res.statusText);
          setErrorText(res.statusText);
        }
        return res.text();
      })
      .then(data => {
        let fileLines = data.split('\n');
        fileLines = fileLines.filter(line => line !== '');
        let fetchedFiles = _.map(fileLines, (file: api.FileResponse): FileObject => {
          if (file.error) {
            setIsLoading(false);
            throw Error(file.error.message);
          }
          if (file) {
            file = JSON.parse(file as any);
            path = path.replace('?path=', '');
            const filePath = PathUtils.joinPaths(rootPath, file.path);
            return {
              file: file.file,
              mountId: mountId,
              path: filePath,
              splittedPath: filePath.split('/').slice(1),
            };
          }
        });

        fetchedFiles = fetchedFiles.filter(ff => ff !== undefined);

        return processFiles(fetchedFiles);

      })
      .catch(error => {
        setErrorText(error);
      });
    return function cleanup() {
      abortController.abort();
    };
  }, [mountId, rootPath]);

  async function processFiles(fetchedFiles) {
    fetchedFiles = _.sortBy(fetchedFiles, (f: FileObject) => {
      if (f.file.name) {

        return (f.file.type === 'dir' ? '0' : '1') + f.file.name.toLowerCase();
      }
      return (f.file.type === 'dir' ? '0' : '1');
    });

    setIsLoading(false);
    if (canFindDuplicates) {
      let duplicatesObjs = await find(fetchedFiles);
      setDuplicates(duplicatesObjs);
      setOriginalDuplicates(duplicatesObjs);
      sortBySize();
      sortByCount();
      setIsDone(true);
    }
  }

  async function find(files: FileObject[]) {
    let lastSleep = Date.now();

    const refresh = async () => {
      const now = Date.now();

      // yield every 15 ms
      if (now - lastSleep > 15) {
        lastSleep = now;
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    };

    const hashes = new Map();

    await add(files, hashes);

    const duplicates = [];

    for (const [key, value] of hashes.entries()) {
      if (value.length > 1) {
        _.each(value, (f: FileObject) => {
          f.file.fileType = getFileType(f);
        });
        duplicates.push(value);
      }

      await refresh();
    }

    return duplicates
  };

  async function add (files: FileObject[], hashes) {
    let lastSleep = Date.now();

    const refresh = async () => {
      const now = Date.now();

      // yield every 15 ms
      if (now - lastSleep > 15) {
        lastSleep = now;
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    };

    for (const file of files) {
      if (file.file.hash && file.file.hash !== emptyHash && !/DS_Store/.test(file.path)) {
        const hash = file.file.hash + file.file.size;

        if (!hashes.has(hash)) {
          hashes.set(hash, []);
        }
        hashes.set(hash, [...hashes.get(hash), file]);
      }

      await refresh();
    }
  };

  /**
   * Calculate excess space in analyzed mount
   */
  useEffect(() => {
    let excessTemp = 0;
    _.each(duplicates, (x: FileObject[]) => {
      excessTemp += x[0].file.size * (x.length - 1);
    });
    setExcess(excessTemp);
  }, [duplicates]);

  /**
   * Sorting functions
   */
  const sortByCount = () => {
    setDuplicates(duplicates => _.sortBy(duplicates, x => -x.length));
    dispatch({ type: 'sortButtonClick', payload: 'count' });

  };

  const sortBySize = () => {
    setDuplicates(duplicates => _.sortBy(duplicates, x => -x[0].file.size));
    dispatch({ type: 'sortButtonClick', payload: 'size' });

  };

  const sortByTotalSize = () => {
    setDuplicates(duplicates => _.sortBy(duplicates, x => -(x[0].file.size * x.length)));
    dispatch({ type: 'sortButtonClick', payload: 'tsize' });

  };

  const sortByType = () => {
    setDuplicates(duplicates => _.sortBy(duplicates, x => x[0].file.fileType));
    dispatch({ type: 'sortButtonClick', payload: 'type' });

  };

  const toolbarText = () => {
    return isLoading || !isDone ? 'Analyzing...' : `Duplicates in ${mount && mount.name} ${rootPath}: ${dul(duplicates)} files (${sizeDisplay(excess)})`;
  };

  const handleSelected = (e: React.MouseEvent<HTMLButtonElement>, v: string) => {
    e.preventDefault();
    if (v === state.selectedButton) {
      dispatch({ type: 'selectButton', payload: '' });
    } else {
      dispatch({ type: 'selectButton', payload: v });

    }

    if (v === 'newer') {
      selectNewerDuplicates();
    } else if (v === 'older') {
      selectOlderDuplicates();
    }
    return true;
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsDeleting(true);
    if (selectedDuplicates.length > 0) {
      const job = {
        files: _.map(selectedDuplicates, f => {
          return {
            mountId: f.mountId,
            path: f.path,
          };
        }),
      };

      return fetch('/api/v2/jobs/files/remove', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(job),
      }).then(response => response.json()).then(job => {
        return jobAwait(job);
      });
    }
  };

  const jobAwait = (job: api.Job) => {
    return fetch(`/api/v2/jobs/${job.id}`).then(res => res.json()).then((newJob: api.Job) => {
      if (newJob.state === 'done' || newJob.state === 'failed') {
        setIsDeleting(false);
        window.location.reload();

        return newJob;
      }
      return setTimeout(() => {
        jobAwait(job);
      }, 1000);
    });
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>, f: FileObject, files: FileObject[]) => {
  };

  const handleClickCheckBox = (e: React.MouseEvent<any>, f: FileObject, files: FileObject[]) => {
    if (e.ctrlKey && !e.altKey) {
      /**
       * EXPERIMENTAL
       * If ctrlKey is held, select all files with the same path in all duplicate groups
       */
      selectFilesWithinPath(f);
    } else if (e.altKey && !e.ctrlKey) {
      excludeFilesInFolder(f);
    } else {
      f.file.isSelected = !f.file.isSelected;
    }

    if (f.file.isSelected) {
      setSelectedDuplicates((selectedDuplicates) => {
        if (!selectedDuplicates.filter(sd => sd === f).length) {
          return [...selectedDuplicates, f];
        }

        return [...selectedDuplicates].filter(f => f.file.isSelected);
      });
    } else {
      setSelectedDuplicates((selectedDuplicates) => {
        return [...selectedDuplicates].filter(f => f.file.isSelected);
      });
    }

    fileGroupSelectionChanged(files);
  };

  const cleanPath = (f: FileObject) => {
    const splitPath = f.path.split('/');
    return splitPath.slice(0, splitPath.length - 1).join('/');
  };

  const selectFilesWithinPath = (f: FileObject) => {
    /**
     * EXPERIMENTAL
     * TODO Check/Discuss
     * - ali oznacimo vse datoteke v skupini iz istega folderja?
     * - If f is selected, deselect all files in this folder??
     */
    const filePath = cleanPath(f);

    const duplicatesNew = [];

    duplicates.forEach(d => {
      const matching: FileObject[] = d.filter((dd: FileObject) => cleanPath(dd) === filePath);
      const notMatching: FileObject[] = _.difference(d, matching);
      if (matching.length === d.length) {
        // All files in a group are in the same folder
        let item: FileObject;
        if (matching.indexOf(f) === matching.length - 1) {
          item = matching.shift();
        } else {
          item = matching.pop();
        }
        item.file.isSelected = false;
        item.file.preventSelection = true;
      }
      duplicatesNew.push(matching);

      _.each(notMatching, file => {
        if (notMatching.length !== d.length) {
          file.file.isSelected = false;
          file.file.preventSelection = false;
        }

        if (matching.length && notMatching.length === 1 && matching.length === d.length - 1) {
          file.file.preventSelection = true;
        }
      });
    });

    _.each(duplicatesNew, d => {
      _.each(d, file => {
        file.file.isSelected = true;
        file.file.preventSelection = false;
      });
      fileGroupSelectionChanged(d);
    });

    updateSelected();
  };

  const excludeFilesInFolder = (f: FileObject) => {
    /**
     * EXPERIMENTAL
     * Excludes files that are in the same folder as the file which was clicked on
     */

    const filePath = cleanPath(f);

    const duplicatesNew = [];

    duplicates.forEach(d => {
      const matching: FileObject[] = d.filter((dd: FileObject) => cleanPath(dd) !== filePath);
      const notMatching: FileObject[] = d.filter((dd: FileObject) => cleanPath(dd) === filePath);
      _.each(notMatching, f => {
        f.file.isSelected = false;
        f.file.preventSelection = false;
      });
      duplicatesNew.push(matching);
    });

    setDuplicates((duplicates) => duplicatesNew.filter(d => d.length > 1));

    updateSelected();
  };

  const excludeFilesInGroup = (f: FileObject) => {
    // Find the group in which this file is present,
    // Remove the group from duplicates

    const duplicatesNew = [];

    for (const d of duplicates) {
      const matching: FileObject[] = d.filter((dd: FileObject) => dd.path === f.path);
      if (matching.length) {
        _.each(d, f => {
          f.file.isSelected = false;
          f.file.preventSelection = false;
        });
      } else {
        duplicatesNew.push(d);
      }
    }
    setDuplicates(duplicatesNew);

    updateSelected();
  };

  /**
   * Select newer duplicates
   */
  const selectNewerDuplicates = () => {
    _.each(duplicates, x => {
      const oldest = _.sortBy(x, (file: FileObject) => {
        return file.file.modified;
      })[0].file.modified;

      const oldestPath = _.last(_.filter(x, f => f.file.modified === oldest)).path;

      _.each(x, file => {
        if (file.file.modified === oldest && file.path === oldestPath) {
          file.file.isSelected = false;
          file.file.preventSelection = true;
        } else {
          file.file.isSelected = true;
          file.file.preventSelection = false;
        }
      });
    });

    updateSelected();
  };

  const selectOlderDuplicates = () => {
    _.each(duplicates, x => {
      const youngest = _.sortBy(x, (file: FileObject) => {
        return -file.file.modified;
      })[0].file.modified;

      const youngestPath = _.first(_.filter(x, f => f.file.modified === youngest)).path;

      _.each(x, file => {
        if (file.file.modified === youngest && file.path === youngestPath) {
          file.file.isSelected = false;
          file.file.preventSelection = true;
        } else {
          file.file.isSelected = true;
          file.file.preventSelection = false;
        }
      });
    });

    updateSelected();
  };

  const updateSelected = () => {
    const totalSelected = [];

    _.each(duplicates, x => {
      _.each(x, file => {
        if (file.file.isSelected) {
          totalSelected.push(file);
        }
      });
    });
    setSelectedDuplicates(totalSelected);
  };

  const clearSelectedDuplicates = () => {
    _.each(duplicates, x => {
      _.each(x, (file: FileObject) => {
        file.file.isSelected = false;
        file.file.preventSelection = false;
      });
    });
    setSelectedDuplicates([]);
    dispatch({ type: 'selectButton', payload: '' });

  };

  const resetDuplicates = () => {
    setDuplicates((duplicates) => originalDuplicates);
    sortByCount();
  };

  const fileGroupSelectionChanged = (files: FileObject[]) => {
    const unselected = _.filter(files, file => {
      return !file.file.isSelected;
    });

    if (unselected.length === 1) {
      _.each(unselected, u => {
        u.file.preventSelection = true;
      });
    } else {
      _.each(unselected, u => {
        u.file.preventSelection = false;
      });
    }
  };

  useEffect(() => {
    const reset = () => {
      setX(0);
      setY(0);
      setVisible(false);
      setActiveFile(null);
    };
    document.addEventListener('click', reset);
    return () => {
      document.removeEventListener('click', reset);
    };
  }, []);

  const handleRightClickFile = (e: React.MouseEvent<HTMLLIElement>, f: FileObject, files: FileObject[]) => {
    e.preventDefault();
    setX(e.pageX);
    setY(e.pageY);
    setVisible(true);
    setActiveFile(f);
  };

  const handleClearClickFile = (e: React.MouseEvent<HTMLLIElement>, files: FileObject[]) => {
    e.preventDefault();
      _.each(files, (file: FileObject) => {
        file.file.isSelected = false;
        file.file.preventSelection = false;
      });
    updateSelected();
    dispatch({ type: 'selectButton', payload: '' });
  };

  const handleNewerClickFile = (e: React.MouseEvent<HTMLLIElement>, files: FileObject[]) => {
    const oldest = _.sortBy(files, (file: any) => {
      return file.file.modified;
    })[0].file.modified;

    const oldestPath = _.last(_.filter(files, x => x.file.modified === oldest)).path;

    _.each(files, x => {
      if (x.file.modified === oldest && x.path === oldestPath) {
        x.file.isSelected = false;
        x.file.preventSelection = true;
      } else {
        x.file.isSelected = true;
        x.file.preventSelection = false;
      }
    });

    updateSelected();
  };

  const handleOlderClickFile = (e: React.MouseEvent<HTMLLIElement>, files: FileObject[]) => {
    const youngest = _.sortBy(files, (file: FileObject) => {
      return -file.file.modified;
    })[0].file.modified;

    const youngestPath = _.first(_.filter(files, x => x.file.modified === youngest)).path;

    _.each(files, file => {
      if (file.file.modified === youngest && file.path === youngestPath) {
        file.file.isSelected = false;
        file.file.preventSelection = true;
      } else {
        file.file.isSelected = true;
        file.file.preventSelection = false;
      }
    });

    updateSelected();
  };


  const SelectMenu = () => {
    return (
      <div className="select-menu">
        <span className="select-menu-tag">Select:</span>

        <button className={classNames('select-menu-option')}
          title="Keep the newest file in each group"
          onClick={(e) => handleSelected(e, 'older')}>Older</button>
        <button className={classNames('select-menu-option')}
          title="Keep the oldest file in each group"
          onClick={(e) => handleSelected(e, 'newer')}>Newer</button>
        <button className={classNames('select-menu-option', { 'not-possible': !selectedDuplicates.length })}
          disabled={!selectedDuplicates.length}
          title="Unselect all files"
          onClick={clearSelectedDuplicates}>Clear</button>
        {
          originalDuplicates.length !== duplicates.length ? (
            <button className={classNames('select-menu-option')}
              title="Reset"
              onClick={resetDuplicates}>Reset</button>
          ) : null
        }
      </div>
    );
  };

  const dul = (duplicates) => {
    let sum = 0;
    duplicates.forEach(d => {
      sum += d.length;
    });
    return sum;
  }

  const SortMenu = () => {
    return (
      <div className="sort-menu">
        <span className="sort-menu-tag">Sort by:</span>

        <button className={classNames('sort-menu-option', { active: state.sort === 'count' })} onClick={sortByCount}>Count</button>
        <button className={classNames('sort-menu-option', { active: state.sort === 'size' })} onClick={sortBySize}>Size</button>
        <button className={classNames('sort-menu-option', { active: state.sort === 'tsize' })} onClick={sortByTotalSize}>Total size</button>
        <button className={classNames('sort-menu-option', { active: state.sort === 'type' })} onClick={sortByType}>Type</button>
      </div>
    );
  };

  const DeleteOverlay = () => {
    if (isDeleting) {
      return (
        <div>
          <div className="delete-overlay">
            <h3>Deleting files</h3>
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    return null;
  };

  const BottomBar = () => {
    return (
      <div className="bottom-bar">
        <div className="bottom-bar__text">Selected: {selectedDuplicates.length} files</div>
        <button className={classNames('delete-button', { 'not-possible': !selectedDuplicates.length })} disabled={!selectedDuplicates.length} onClick={handleDelete}>Delete selected</button>
      </div>
    );
  };

  return (
    <div className="dff-analyse">
      <Toolbar>
          <div className='l-toolbar__column'>
            <Link to="/">
              <img src={dffLogo} alt="Koofr DFF logo" className="l-toolbar__logo"/>
            </Link>
            <div className='divider'></div>
            <div className='app-name'>Duplicate file finder</div>
          </div>
          <div className='l-toolbar__column stretch'>{toolbarText()}</div>
          <div className='l-toolbar__column'>
            <Link to="/dff" className='new-search'>New Search</Link>
            <div className='divider'></div>
            <MainMenuComponent/>
          </div>
        </Toolbar>
      {
        isDone ? (
          <div className="dff-analyse">
            {duplicates.length ? (
              <div className="menu">
                <SelectMenu />
                <SortMenu />
                <div className="line">&nbsp;</div>
              </div>
            ) : null}

            <div className="main-container">
              <div className="mount">
                <div className="root">
                  <ul>
                    {
                      duplicates.map((dupl, index) => (
                        <FileGroup
                          files={dupl}
                          key={index}
                          mount={mount.name}
                          handleCheck={handleCheck}
                          handleClickCheckBox={handleClickCheckBox}
                          handleRightClick={handleRightClickFile}
                          handleClearClick={handleClearClickFile}
                          handleNewerClick={handleNewerClickFile}
                          handleOlderClick={handleOlderClickFile}
                          activeFile={activeFile} />
                      ))
                    }
                  </ul>
                  <BottomBar />
                  <DeleteOverlay />
                  {
                    duplicates.length === 0 ? (
                      <div className="no-duplicates">
                        { errorText === "" ? (
                          <h3>No duplicates found</h3>
                        ) : (<h3>{errorText}</h3>)}

                      </div>
                    ) : null
                  }
                </div>
              </div>
            </div>
          </div>
        ) : null
      }

      {
        visible ? (
          <MenuComponent x={x} y={y}>
            <div className="menu__item">
              <button className="menu__button" onClick={() => selectFilesWithinPath(activeFile)}>
                <div className="menu__button-content">
                  <div className="menu__text">
                    Select all in same folder
                  </div>
                </div>
              </button>
            </div>
            <div className="menu__item">
              <button className="menu__button" onClick={() => excludeFilesInFolder(activeFile)}>
                <div className="menu__button-content">
                  <div className="menu__text">
                    Exclude this folder
                  </div>
                </div>
              </button>
            </div>
            <div className="menu__item">
              <button className="menu__button" onClick={() => excludeFilesInGroup(activeFile)}>
                <div className="menu__button-content">
                  <div className="menu__text">
                    Exclude this file
                  </div>
                </div>
              </button>
            </div>
          </MenuComponent>
        ) : null
      }

      {!isDone ? (
        <LoadingSpinner />
      ) : null}
    </div>
  );
};
